import { useNavigate, useSearchParams } from "react-router-dom";
import MeetingActionButtonContainer from "./MeetingActionButtonContainer";
import React, {
  RefObject,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  confirmSlotAvailability,
  forwardInterview,
  sendInterviewInvite,
} from "@endpoints/interview.service";
import { useSnackbar } from "notistack";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import {
  IGetInterviewByIdResponse,
  Meeting,
  User,
} from "@endpoints/types/interview.type";
import { EnumUserTypes, userTypes } from "@utils/constants";
import { RootState } from "@store/reducers";
import { useSelector } from "react-redux";
import {
  extractProposalGroupAndAvailabilities,
  IAvailabilitySlot,
  MeetingParties,
  TypeGroupOfProposals,
} from "../MeetingDetailPage";
import dayjs from "@lib/dayjs";
import PlusCircleIcon from "@assets/svg/PlusCircleIcon";
import {
  checkIsSlotDisabled,
  getHiringTeamResponseSummary,
  getSlotStatus,
  isAllSlotResponded,
  isSlotReadyForInvite,
} from "../helpers/slotHelpers";
import { EditIcon, PencilIcon } from "lucide-react";
import { HiringCompanyType } from "@utils/constants";

interface LatestProposalCardProps {
  meetingProposedBy: User;
  availabilitySlots: IAvailabilitySlot[];
  setAvailabilitySlots: React.Dispatch<SetStateAction<IAvailabilitySlot[]>>;
  groupOfProposals: TypeGroupOfProposals[];
  isHiringCompanyInvolved: boolean;
  isInviteSend: boolean;
  hasChanges: boolean;
  isEverySlotsDeclined: boolean;
  interviewDetails: IGetInterviewByIdResponse;
  setInterviewDetails: React.Dispatch<
    SetStateAction<IGetInterviewByIdResponse>
  >;
  availabilitySlotsRef: RefObject<IAvailabilitySlot[]>;
  isAssignedHiringTeamMember?: boolean;
}

const LatestProposalCard = (props: LatestProposalCardProps) => {
  const {
    meetingProposedBy,
    availabilitySlots,
    setAvailabilitySlots,
    groupOfProposals,
    isHiringCompanyInvolved,
    isInviteSend,
    hasChanges,
    isEverySlotsDeclined,
    availabilitySlotsRef,
    interviewDetails,
    setInterviewDetails,
    isAssignedHiringTeamMember: isAssignedHiringTeamMemberProp,
  } = props;
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const user: any = useSelector((state: RootState) => state.auth.user);

  const [searchParams, setSearchParams] = useSearchParams();
  const interviewId = searchParams.get("id");

  // Local state
  const [isSlotEditable, setIsSlotEditable] = useState(true);
  const [isForwardingInterview, setIsForwardingInterview] = useState(false);
  const [selectedAvailabiliy, setSelectedAvailabiliy] = useState<string>();
  const [isSavingAvailability, setIsSavingAvailability] = useState(false);
  const [isSendingInvite, setIsSendingInvite] = useState(false);

  const proposedBy = useMemo(
    () => availabilitySlots[0]?.proposedBy,
    [availabilitySlots]
  );

  const handleForwardMeetingToCandidate = async () => {
    try {
      setIsForwardingInterview(true);
      const { success, data } = await forwardInterview(interviewId);
      if (success) {
        setInterviewDetails((prev)=>{
          return {...prev,
            interviewStatus:data?.interviewStatus
          }
        });
        enqueueSnackbar("Interview forwarded successfully.", {
          variant: "success",
        });
      }
    } catch (error) {
      console.error("Error: forwarding meeting to candidate", error);
    } finally {
      setIsForwardingInterview(false);
    }
  };

  const handleCounterProposalPoll = () => {
    const isAllAvailabilityDeclined = !availabilitySlots?.find(
      (item) => !item?.isDeclined
    );

    if (
      isAllAvailabilityDeclined ||
      disableActionContainer ||
      isEverySlotsDeclined
    ) {
      navigate(`/counter-proposal-poll?id=${interviewId}`);
    } else {
      enqueueSnackbar(
        "You must decline all the availabilities to propose new time slots.",
        { variant: "error" }
      );
    }
  };

  const handleSaveAvailability = async () => {
    try {
      setIsSavingAvailability(true);
      const apiPayload = [];
      availabilitySlots.map((item) => {
        if (item?.isAcceptedTemp === true) {
          apiPayload.push({
            slotId: item?._id,
            isAvailable: true,
          });
        }
        if (item?.isDeclinedTemp === true) {
          apiPayload.push({
            slotId: item?._id,
            isAvailable: false,
          });
        }
      });
      const { success, data } = await confirmSlotAvailability(interviewId, {
        slotUpdates: apiPayload,
      });
      if (success) {
        const generalAvailabilitySlots = data?.generalAvailabilitySlots?.map(
          (gSlot) => {
            return {
              ...gSlot,
              proposedBy: interviewDetails?.generalAvailabilitySlots?.find(
                (rslot) => rslot?.proposedBy?._id === gSlot?.proposedBy
              )?.proposedBy,
            };
          }
        );
        const { formattedAvailabilitySlots } =
          extractProposalGroupAndAvailabilities(generalAvailabilitySlots);
        setAvailabilitySlots(formattedAvailabilitySlots);
        availabilitySlotsRef.current = formattedAvailabilitySlots;
        setIsSlotEditable(false);
        // if (user?.userType === EnumUserTypes.CANDIDATE) {
        //   navigate("/candidate-calendar/scheduling");
        // } else {
        //   navigate("/meeting/scheduling");
        // }
        enqueueSnackbar("Slot availability updated successfully.", {
          variant: "success",
          anchorOrigin:{
            vertical:'top',
            horizontal:'center'
          }
        });
      }
      console.log("api payload", apiPayload);
    } catch (error) {
      console.error("Error: saving availability", error);
    } finally {
      setIsSavingAvailability(false);
    }
  };

  const handleSendInvite = async () => {
    try {
      setIsSendingInvite(true);
      if (!canSendInvite) {
        await handleSaveAvailability();
      }
      const { success } = await sendInterviewInvite({
        id: interviewId,
        slotId: canSendInvite ? canSendInvite?._id : selectedAvailabiliy,
      });
      if (success)
        enqueueSnackbar("Invitation sent successfully.", {
          variant: "success",
        });
      navigate("/calendar/meeting/scheduling");
    } catch (error) {
      enqueueSnackbar("Error sending interview invite", { variant: "error" });
      console.error("Error: sending interview invite", error);
    } finally {
      setIsSendingInvite(false);
    }
  };

  const meetingResponder = useMemo(() => {
    const parties = MeetingParties?.filter((party) => party !== user?.userType);
    return parties.filter((party) => meetingProposedBy?.userType !== party);
  }, [isHiringCompanyInvolved, meetingProposedBy]);

  const disableActionContainer = useMemo(
    () =>
      !availabilitySlots?.some((item) => {
        if (meetingResponder[0] === EnumUserTypes.CANDIDATE) {
          return (
            item?.isCandidateAvailable ||
            item?.isCandidateAvailable === undefined
          );
        } else if (meetingResponder[0] === EnumUserTypes.RECRUITER) {
          return (
            item?.isRecruiterAvailable ||
            item?.isRecruiterAvailable === undefined
          );
        } else if (
          meetingResponder[0] === EnumUserTypes.HIRING_COMPANY &&
          isHiringCompanyInvolved
        ) {
          return (
            item?.isCompanyAvailable || item?.isCompanyAvailable === undefined
          );
        } else {
          return true;
        }
      }) || isInviteSend,
    [availabilitySlots, meetingResponder, isHiringCompanyInvolved, isInviteSend]
  );

  const hasAllAssignedHiringTeamResponded = useMemo(() => {
    const assignedMemberIds =
      interviewDetails?.hiringTeamMembers
        ?.map((member) => {
          const memberUserId =
            typeof member?.userId === "string"
              ? member.userId
              : member?.userId?._id;
          return memberUserId ? String(memberUserId) : null;
        })
        .filter(Boolean) || [];

    if (!assignedMemberIds.length) return true;

    return availabilitySlots?.every((slot) =>
      assignedMemberIds.every((memberId) =>
        slot?.hiringTeamAvailability?.some((availability) => {
          const availabilityMemberId =
            typeof availability?.memberId === "string"
              ? availability.memberId
              : availability?.memberId?._id;
          return (
            String(availabilityMemberId) === memberId &&
            availability?.isAvailable !== undefined
          );
        })
      )
    );
  }, [availabilitySlots, interviewDetails?.hiringTeamMembers]);

  const canSendInvite = useMemo(
    () =>
      availabilitySlots?.find((item) => {
        const isCandidateAndRecruiterReady =
          item?.isRecruiterAvailable && item?.isCandidateAvailable;
        if (!isCandidateAndRecruiterReady) return false;

        if (!isHiringCompanyInvolved) {
          return true;
        }

        return item?.isCompanyAvailable && hasAllAssignedHiringTeamResponded;
      }),
    [
      availabilitySlots,
      hasAllAssignedHiringTeamResponded,
      isHiringCompanyInvolved,
    ]
  );

  const isCounterProposalEnabled = useMemo(() => {
    const isAllAvailabilityDeclined = !availabilitySlots?.find(
      (item) => !item?.isDeclined
    );

    return (
      isAllAvailabilityDeclined ||
      disableActionContainer ||
      isEverySlotsDeclined
    );
  }, [disableActionContainer, isEverySlotsDeclined, availabilitySlots]);

  const isConfirmChoicesEnabled = useMemo(
    () =>
      hasChanges &&
      availabilitySlots?.every((eSlot) => {
        return (
          eSlot?.isAcceptedTemp ||
          eSlot?.isDeclinedTemp ||
          checkIsSlotDisabled(eSlot)
        );
      }),
    [hasChanges, availabilitySlots]
  );

  const isUserReadyForInvite = useMemo(
    () => isSlotReadyForInvite(availabilitySlots),
    [availabilitySlots]
  );

  const hasCommonAcceptedSlot = useMemo(
    () =>
      availabilitySlots?.some((item) => {
        const isCandidateAndRecruiterReady =
          item?.isCandidateAvailable && item?.isRecruiterAvailable;
        if (!isCandidateAndRecruiterReady) return false;

        if (!isHiringCompanyInvolved) {
          return true;
        }

        return item?.isCompanyAvailable && hasAllAssignedHiringTeamResponded;
      }),
    [
      availabilitySlots,
      hasAllAssignedHiringTeamResponded,
      isHiringCompanyInvolved,
    ]
  );

  const isMeetingForwardedToCandidate = useMemo(
    () => interviewDetails?.interviewStatus === "Pending Acceptance",
    [interviewDetails]
  );

  const isAdminOrSuperAdmin = useMemo(
    () =>
      user?.userType === EnumUserTypes.HIRING_COMPANY &&
      [HiringCompanyType.ADMIN, HiringCompanyType.SUPER_ADMIN].includes(
        Number(user?.hiringCompanyType)
      ),
    [user]
  );

  const isAssignedHiringTeamMember = useMemo(() => {
    if (typeof isAssignedHiringTeamMemberProp === "boolean") {
      return isAssignedHiringTeamMemberProp;
    }

    if (user?.userType !== EnumUserTypes.HIRING_COMPANY) return false;
    if (
      ![HiringCompanyType.MANAGER, HiringCompanyType.TEAM_MEMBER].includes(
        Number(user?.hiringCompanyType)
      )
    ) {
      return false;
    }

    return !!interviewDetails?.hiringTeamMembers?.some((member) => {
      const memberUserId =
        typeof member?.userId === "string"
          ? member.userId
          : member?.userId?._id;
      return String(memberUserId) === String(user?._id || user?.userId);
    });
  }, [interviewDetails?.hiringTeamMembers, user, isAssignedHiringTeamMemberProp]);

  const isAvailabilityResponseRequired = useMemo(() => {
    if (!isAdminOrSuperAdmin) return true;
    // If interview is routed through hiring team members, admin/super-admin
    // can view but should not be required to submit slot availability.
    return !(interviewDetails?.hiringTeamMembers?.length > 0);
  }, [isAdminOrSuperAdmin, interviewDetails?.hiringTeamMembers]);

  const shouldShowHiringTeamResponseSummary = useMemo(
    () =>
      user?.userType === EnumUserTypes.HIRING_COMPANY &&
      !isAssignedHiringTeamMember &&
      (interviewDetails?.hiringTeamMembers?.length || 0) > 0,
    [
      interviewDetails?.hiringTeamMembers?.length,
      isAssignedHiringTeamMember,
      user?.userType,
    ]
  );

  const canForwardToRecruiter = useMemo(() => {
    const hiringCompanyRole = Number(user?.hiringCompanyType);
    const currentUserId = user?._id || user?.userId;
    const meetingCreatorId =
      typeof interviewDetails?.meetingCreator === "string"
        ? interviewDetails?.meetingCreator
        : interviewDetails?.meetingCreator?._id;
    const isManagerMeetingCreator =
      hiringCompanyRole === HiringCompanyType.MANAGER &&
      String(meetingCreatorId) === String(currentUserId);
    const isHiringCompanyForwarder =
      user?.userType === EnumUserTypes.HIRING_COMPANY &&
      (
        [HiringCompanyType.ADMIN, HiringCompanyType.SUPER_ADMIN].includes(
          hiringCompanyRole
        ) ||
        isManagerMeetingCreator
      );

    return (
      isHiringCompanyForwarder &&
      interviewDetails?.interviewStatus === "Sent to Team Member" &&
      hasAllAssignedHiringTeamResponded &&
      !isInviteSend
    );
  }, [
    hasAllAssignedHiringTeamResponded,
    interviewDetails?.meetingCreator,
    interviewDetails?.interviewStatus,
    isInviteSend,
    user?.hiringCompanyType,
    user?._id,
    user?.userId,
    user?.userType,
  ]);

  useEffect(() => {
    if (isAllSlotResponded(availabilitySlots, isAssignedHiringTeamMember) && !hasChanges) {
      setIsSlotEditable(false);
    } else {
      setIsSlotEditable(true);
    }
  }, [availabilitySlots, isAssignedHiringTeamMember, hasChanges]);
  console.log("availabilitySlots", availabilitySlots);

  return (
    <div className="flex flex-col gap-3 mt-8">
      <div className="flex items-center">
        {user?.userType === EnumUserTypes.RECRUITER &&
        !isMeetingForwardedToCandidate ? (
          <button
            onClick={handleForwardMeetingToCandidate}
            className="flex items-center gap-2 bg-primary rounded-md px-5 py-2 w-max text-xs text-white font-semibold hover:bg-black"
          >
            Forward invitation to candidate
            {isForwardingInterview ? <LoadingAnimation /> : null}
          </button>
        ) : null}
        {canForwardToRecruiter ? (
          <button
            onClick={handleForwardMeetingToCandidate}
            className="ml-auto flex items-center gap-2 bg-primary rounded-md px-5 py-2 w-max text-xs text-white font-semibold hover:bg-black"
          >
            Forward invitation to recruiter agency
            {isForwardingInterview ? <LoadingAnimation /> : null}
          </button>
        ) : null}
        {/* {isInviteSend ? (
          <button
            onClick={handleForwardMeetingToCandidate}
            className="flex items-center ml-auto gap-2 bg-primary rounded-md px-5 py-2 w-max text-xs text-white font-semibold hover:bg-black"
          >
            Confirm Changes
            {isForwardingInterview ? <LoadingAnimation /> : null}
          </button>
        ) : (
          <button
            onClick={handleForwardMeetingToCandidate}
            className="flex items-center ml-auto gap-2 bg-primary rounded-md px-5 py-2 w-max text-xs text-white font-semibold hover:bg-black"
          >
            Confirm Changes
            {isForwardingInterview ? <LoadingAnimation /> : null}
          </button>
        )} */}
      </div>
      <div className="flex justify-between border-y border-dashed py-4 gap-8">
        {/* Column 1 */}
        <div className="flex flex-col flex-1 gap-5">
          <span className="font-semibold text-xs text-primary">
            {meetingProposedBy?.userType === userTypes.CANDIDATE
              ? "Candidate's"
              : meetingProposedBy?.userType === userTypes.RECRUITER
              ? "Agent's"
              : "Hiring Company's"}{" "}
            proposed times
          </span>
          <div className="flex flex-col gap-5">
            {availabilitySlots?.map((slot) => {
              const date = dayjs(slot?.startTime).format(`dddd MMM Do`);
              const showSendInvite = useMemo(
                () =>
                  slot?.isCandidateAvailable &&
                  slot?.isRecruiterAvailable &&
                  slot?.isCompanyAvailable &&
                  slot?.proposedBy?.userType === EnumUserTypes?.RECRUITER,
                [slot, user]
              );
              return (
                <div
                  key={slot?._id}
                  className={`grid grid-cols-2 items-center ${
                    isInviteSend
                      ? "grid-cols-[1fr_0.7fr_0.7fr]"
                      : "grid-cols-[1fr_1fr]"
                  }`}
                >
                  <span className="text-xs font-medium text-light-black">
                    {date}
                  </span>
                  <span className="text-xs font-medium text-light-black">
                    {dayjs(slot?.startTime).format("HH:mm")}
                    &nbsp;&nbsp;&nbsp;-&nbsp;&nbsp;&nbsp;
                    {dayjs(slot?.endTime).format("HH:mm")}
                  </span>
                  {showSendInvite && isInviteSend ? (
                    <div
                      className={`flex items-center justify-center gap-2 bg-primary text-white border text-xs font-semibold rounded-md py-0.5 px-1.5 w-max`}
                    >
                      Invited
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
        {/* Column 2 */}
        {isHiringCompanyInvolved ? (
          <div className="flex flex-col flex-0.5 gap-5 text-center">
            <span className="font-semibold text-xs text-primary">
              {meetingResponder[0] === EnumUserTypes.CANDIDATE
                ? "Candidate's"
                : meetingResponder[0] === EnumUserTypes.HIRING_COMPANY
                ? "Hiring company's"
                : "Agent's"}{" "}
              response
            </span>
            <div className="flex flex-col gap-5">
              {availabilitySlots?.map((slot) => {
                return (
                  <span
                    key={slot?._id}
                    className="text-xs capitalize text-primary"
                  >
                    {getSlotStatus({
                      slot,
                      statusOf: meetingResponder[0],
                      isAssignedHiringTeamMember,
                    })}
                    {/* {EnumUserTypes[meetingResponder[0]]} */}
                  </span>
                );
              })}
            </div>
          </div>
        ) : null}
        {/* Column 3 */}
        {!isSlotEditable ? (
          <div className={`flex flex-col items-center flex-[0.5] gap-5`}>
            <div className="flex gap-3">
              <span className="font-semibold text-xs text-primary">
                Your Response
              </span>
              {isAvailabilityResponseRequired &&
              isAllSlotResponded(
                availabilitySlots,
                isAssignedHiringTeamMember
              ) ? (
                <button
                  onClick={() => setIsSlotEditable(true)}
                  className="flex items-center gap-1 bg-orange-500 rounded-md text-xs font-medium text-white py-0.5 px-2 hover:bg-orange-600"
                >
                  Edit
                  <PencilIcon className="w-3 h-3" />
                </button>
              ) : null}
            </div>
            <div className="flex flex-col gap-5">
              {availabilitySlots?.map((slot) => {
                // const allEntities acce
                const isSlotEligibleToInvite =
                  slot?.isCandidateAvailable &&
                  slot?.isRecruiterAvailable &&
                  (!isHiringCompanyInvolved ||
                    (slot?.isCompanyAvailable &&
                      hasAllAssignedHiringTeamResponded)) &&
                  user?.userType === EnumUserTypes.RECRUITER;
                return (
                  <div key={slot?._id} className="flex items-center gap-2">
                    <span className="text-xs capitalize text-primary">
                      {isAvailabilityResponseRequired
                        ? getSlotStatus({
                            slot,
                            statusOf: user?.userType,
                            isAssignedHiringTeamMember,
                          })
                        : "Not required"}
                    </span>
                    {isInviteSend ? (
                      <div className="flex items-center justify-center bg-primary text-white text-xs capitalize py-0.5 px-4 rounded-md">
                        Invited
                      </div>
                    ) : isSlotEligibleToInvite ? (
                      <button
                        onClick={handleSendInvite}
                        disabled={isSendingInvite}
                        className="flex items-center justify-center gap-2 bg-primary text-white text-xs capitalize py-0.5 px-2 rounded-md"
                      >
                        Send Invite
                        {isSendingInvite ? (
                          <LoadingAnimation className="w-3 h-3" />
                        ) : null}
                      </button>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div
            className={`flex flex-col gap-3.5 flex-[0.8] ${
              disableActionContainer ? "opacity-50" : ""
            }`}
          >
            {!isAvailabilityResponseRequired ? (
              <div className="flex flex-col gap-3">
                <span className="font-semibold text-xs text-primary">
                  {shouldShowHiringTeamResponseSummary
                    ? "Hiring team response"
                    : "Your Response"}
                </span>
                <div className="flex flex-col gap-1.5">
                  {availabilitySlots?.map((slot) => (
                    <span
                      key={slot?._id}
                      className="text-xs capitalize text-primary"
                    >
                      {shouldShowHiringTeamResponseSummary
                        ? getHiringTeamResponseSummary(slot)
                        : "Not required"}
                    </span>
                  ))}
                </div>
              </div>
            ) : isAssignedHiringTeamMember ||
              user?.userType !== EnumUserTypes.HIRING_COMPANY ? (
              <>
                <div className="flex gap-3">
                  <span className="font-semibold text-xs text-primary">
                    Select your available slot
                  </span>
                </div>
                <div className={`flex flex-col gap-1.5`}>
                  {availabilitySlots?.map((slot) => {
                    return (
                      <MeetingActionButtonContainer
                        key={slot?._id + slot?.isAccepted}
                        isSlotDisabled={
                          checkIsSlotDisabled(slot) || hasCommonAcceptedSlot
                        }
                        isFinalInviteSlot={isUserReadyForInvite}
                        interviewId={interviewId}
                        slot={slot}
                        selectedAvailabiliy={selectedAvailabiliy}
                        availabilitySlots={availabilitySlots}
                        setAvailabilitySlots={setAvailabilitySlots}
                        setSelectedAvailabiliy={setSelectedAvailabiliy}
                        disableActionContainer={disableActionContainer}
                        isInviteSend={isInviteSend}
                      />
                    );
                  })}
                </div>
                {canSendInvite && user?.userType === EnumUserTypes.RECRUITER ? (
                  <button
                    onClick={handleSendInvite}
                    disabled={isSendingInvite}
                    className="flex items-center justify-center ml-auto gap-2 bg-primary rounded-md px-5 py-2 w-full text-xs text-white font-semibold hover:bg-black"
                  >
                    Send Invite
                    {isSendingInvite ? (
                      <LoadingAnimation className="h-4 w-4" />
                    ) : null}
                  </button>
                ) : (
                  <button
                    onClick={handleSaveAvailability}
                    disabled={isSavingAvailability || !isConfirmChoicesEnabled}
                    className={`flex items-center justify-center ml-auto gap-2 bg-primary rounded-md px-5 py-2 w-full text-xs text-white font-semibold  ${
                      isConfirmChoicesEnabled ? "hover:bg-black" : "opacity-50"
                    }`}
                  >
                    Confirm choices
                    {isSavingAvailability ? (
                      <LoadingAnimation className="h-4 w-4" />
                    ) : null}
                  </button>
                )}
              </>
            ) : (
              <div className="flex flex-col gap-3">
                <span className="font-semibold text-xs text-primary">
                  Your Response
                </span>
                <div className="flex flex-col gap-1.5">
                  {availabilitySlots?.map((slot) => (
                    <span
                      key={slot?._id}
                      className="text-xs capitalize text-primary"
                    >
                      {getSlotStatus({
                        slot,
                        statusOf: user?.userType,
                        isAssignedHiringTeamMember,
                      })}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {/* {hasChanges ||
            ((selectedAvailabiliy || canSendInvite) &&
              user?.userType === EnumUserTypes.RECRUITER) ? (
              <button
                onClick={() =>
                  (selectedAvailabiliy || canSendInvite) &&
                  user?.userType === EnumUserTypes.RECRUITER
                    ? handleSendInvite()
                    : handleSaveAvailability()
                }
                disabled={isSavingAvailability || isSendingInvite}
                className="flex items-center justify-center ml-auto gap-2 bg-primary rounded-md px-5 py-2 w-full text-xs text-white font-semibold hover:bg-black"
              >
                {(selectedAvailabiliy || canSendInvite) &&
                user?.userType === EnumUserTypes.RECRUITER
                  ? "Send Invite"
                  : "Save Changes"}
                {isSavingAvailability || isSendingInvite ? (
                  <LoadingAnimation className="h-4 w-4" />
                ) : null}
              </button>
            ) : null} */}
          </div>
        )}
      </div>
      {/* Note message section */}
      {groupOfProposals?.length > 1 ? (
        <span className="font-semibold text-primary text-xs text-center">
          <span className="font-medium text-light-black">No matches : </span>
          {proposedBy?.userType === userTypes.CANDIDATE
            ? "Candidate"
            : proposedBy?.userType === userTypes.HIRING_COMPANY
            ? "Hiring company's"
            : "Agent"}{" "}
          has proposed new time-slots
        </span>
      ) : null}
      {/* Propose new time slot button */}
      {!isInviteSend && isCounterProposalEnabled ? (
        <div className="flex flex-col items-center mt-5 gap-3">
          <span className="text-xs font-medium text-light-black">
            If none of the above time slots match your availability, you may
            propose new one.
          </span>
          <button
            onClick={handleCounterProposalPoll}
            className="flex itesm-center justify-center text-white text-xs font-semibold bg-primary rounded-sm py-2 px-4 gap-1"
          >
            <PlusCircleIcon className={"w-4 h-4"} fill={"white"} />
            Propose new times.
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default LatestProposalCard;
